/**
 * TimelinePanel — unified chronological feed of every typed event on
 * the active project. Human edits AND agent fires render here side by
 * side, with persona avatars so designers see WHO did what.
 *
 * Same shape regardless of actor (lib/agent/timeline.ts#TimelineEvent).
 * Both actors write to the same node kind → structural in-sync, never
 * "agent event arrives late". The panel listens for the
 * `foyrmind:timeline` custom event so it refreshes the instant a new
 * event lands without polling.
 */
import { useEffect, useMemo, useState } from "react";
import { CircleCheck, AlertTriangle, ShieldAlert, User, Sparkles, Clock4 } from "lucide-react";
import { readTimeline, resolveActor, type TimelineEvent } from "../../lib/agent/timeline";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";

function relativeFromIso(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 5)    return "just now";
  if (sec < 60)   return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60)   return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)    return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

function VerdictPill({ verdict }: { verdict: TimelineEvent["verdict"] }): JSX.Element {
  const cfg = verdict === "block"
    ? { icon: <ShieldAlert className="w-3 h-3" />, cls: "bg-foyr-deep/10 border-foyr-deep/40 text-foyr-deep" }
    : verdict === "warn"
      ? { icon: <AlertTriangle className="w-3 h-3" />, cls: "bg-foyr/10 border-foyr/40 text-foyr-deep" }
      : { icon: <CircleCheck className="w-3 h-3" />,  cls: "bg-fern/10 border-fern/40 text-fern" };
  return (
    <span className={"pill text-[9.5px] border inline-flex items-center gap-1 uppercase tracking-wider " + cfg.cls}>
      {cfg.icon}{verdict}
    </span>
  );
}

export function TimelinePanel(): JSX.Element {
  const { activeId, graph } = useActiveProjectGraph();
  const [tick, setTick] = useState(0);

  // Re-read on the custom event the recordEvent fires.
  useEffect(() => {
    const onTick = (ev: Event): void => {
      const e = ev as CustomEvent<{ projectId: string }>;
      if (!e.detail || e.detail.projectId === activeId) setTick((t) => t + 1);
    };
    window.addEventListener("foyrmind:timeline", onTick);
    return () => window.removeEventListener("foyrmind:timeline", onTick);
  }, [activeId]);

  const events = useMemo(() => readTimeline(activeId), [activeId, tick]);
  const humanCount = events.filter((e) => e.actorKind === "human").length;
  const agentCount = events.filter((e) => e.actorKind === "agent").length;

  if (events.length === 0) {
    return (
      <section className="panel p-5 text-center">
        <div className="eyebrow inline-flex items-center gap-1.5 justify-center mb-1.5 text-ink-500">
          <Clock4 className="w-3 h-3" /> Timeline
        </div>
        <h3 className="font-newyork text-[18px] text-ink-950 leading-tight">
          No events on {graph.projectName} yet
        </h3>
        <p className="text-[12px] text-ink-500 mt-1 max-w-md mx-auto">
          The unified feed shows every typed mutation — your placements AND every specialist agent's
          fires — the instant they happen. Add a SKU from the catalog or trigger a rule to see it appear.
        </p>
      </section>
    );
  }

  return (
    <section className="panel p-3 space-y-2">
      <header className="flex items-center justify-between flex-wrap gap-2 px-1">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Clock4 className="w-3 h-3" /> Timeline · {graph.projectName}
          </div>
          <div className="text-[10.5px] text-ink-500 tabular mt-0.5 flex flex-wrap gap-1.5">
            <span className="pill border border-sea/40 bg-sea/10 text-sea inline-flex items-center gap-1"><User className="w-3 h-3" />{humanCount} human</span>
            <span className="pill border border-foyr/40 bg-foyr/10 text-foyr-deep inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />{agentCount} agent</span>
          </div>
        </div>
      </header>
      <ol className="relative pl-3 space-y-1.5 max-h-[420px] overflow-y-auto">
        {events.map((ev) => {
          const actor = resolveActor(ev.actorKind, ev.actorId);
          return (
            <li key={ev.id} className="relative">
              <div className="flex items-start gap-2 rounded-xl border border-border bg-cream px-2.5 py-2 hover:border-foyr/40 transition">
                <div
                  className="shrink-0 w-7 h-7 rounded-full grid place-items-center text-[10px] font-semibold tabular text-cream shadow-[0_1px_2px_rgba(0,0,0,0.10)]"
                  style={{ background: actor.color }}
                  title={`${actor.displayName} — ${actor.title}`}
                >
                  {actor.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[12px] text-ink-950 leading-snug truncate">
                      <span className="font-medium">{actor.displayName}</span>
                      <span className="text-ink-500"> · {actor.title}</span>
                    </div>
                    <span className="text-[10px] tabular text-ink-500 shrink-0">{relativeFromIso(ev.atTime)}</span>
                  </div>
                  <div className="text-[12px] text-ink-700 leading-snug">
                    <span className="text-ink-500 italic">{ev.verb}</span>{" "}
                    {ev.summary}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <VerdictPill verdict={ev.verdict} />
                    {ev.citation ? (
                      <span className="pill bg-cream-dark border border-border text-ink-500 text-[9.5px] tabular px-1.5 py-0">{ev.citation}</span>
                    ) : null}
                  </div>
                  {ev.fix ? (
                    <div className="text-[10.5px] text-ink-500 mt-1 leading-snug line-clamp-2">{ev.fix}</div>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
